import { DOCUMENT, Injectable, InjectionToken, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
  CookieWriterService,
  getAuthTokenFromCookie,
  msalScopes,
  resolveSuiteReturnUrl,
} from '@neworg-net/amsuite-core/auth';
import { ConfigService } from '@neworg-net/amsuite-core/config';

import { UserProperty } from '../properties';

const PENDING_KEY = 'amsuite.login-return';
/** Abbastanza per un login con MFA; oltre, un ritorno dimenticato non deve scattare. */
const PENDING_TTL_MS = 15 * 60_000;

interface PendingLoginReturn {
  readonly version: 1;
  readonly url: string;
  readonly expiresAt: number;
}

/** Browser boundary injectable so storage and full-page navigation can be tested in place. */
export interface LoginReturnBrowser {
  href(): string;
  read(): string | null;
  save(value: string): void;
  clear(): void;
  replace(url: string): void;
}

export const LOGIN_RETURN_BROWSER = new InjectionToken<LoginReturnBrowser>('LOGIN_RETURN_BROWSER', {
  providedIn: 'root',
  factory: () => {
    const window = inject(DOCUMENT).defaultView;
    return {
      href: () => window?.location.href ?? '',
      read: () => window?.sessionStorage.getItem(PENDING_KEY) ?? null,
      save: value => {
        if (!window) throw new Error('Browser unavailable');
        window.sessionStorage.setItem(PENDING_KEY, value);
      },
      clear: () => window?.sessionStorage.removeItem(PENDING_KEY),
      replace: url => window?.location.replace(url),
    };
  },
});

/**
 * Indirizzo da riaprire dopo il login della Suite (#2161). La guard di amsuite-core
 * manda qui (`/login?returnUrl=…`) chi non ha un `domain_hint` per B2C:
 *  - la shell stessa, con il path richiesto → `redirectStartPage` di MSAL, che dopo il
 *    login riporta lì da sé;
 *  - una satellite, con il suo URL → conservato per scheda e riaperto da `resumePending`
 *    a shell caricata, solo se è un'app del catalogo dell'utente e con il cookie
 *    `authToken` dell'account attivo appena scritto.
 * Gli indirizzi fuori dal perimetro dei cookie di auth li scarta `resolveSuiteReturnUrl`.
 */
@Injectable({ providedIn: 'root' })
export class LoginReturnService {
  private readonly browser = inject(LOGIN_RETURN_BROWSER);
  private readonly config = inject(ConfigService);
  private readonly msal = inject(MsalService);
  private readonly user = inject(UserProperty);
  /** Presente perché la shell è `isCookieWriter`. */
  private readonly cookieWriter = inject(CookieWriterService, { optional: true });

  /**
   * Legge il `returnUrl` della pagina di login. Ritorna l'URL da passare a MSAL come
   * `redirectStartPage` se è della shell; se è di un'altra app lo conserva e ritorna
   * `undefined`, come per un valore rifiutato.
   */
  capture(raw: string | null): string | undefined {
    // Ogni passaggio dalla login sostituisce il ritorno di un tentativo precedente.
    this.clear();
    const current = this.browser.href();
    const target = resolveSuiteReturnUrl(raw, current);
    if (!target) return undefined;
    if (target.origin === new URL(current).origin) {
      return this.isLoginPage(target) ? undefined : target.href;
    }
    try {
      this.browser.save(JSON.stringify({
        version: 1,
        url: target.href,
        expiresAt: Date.now() + PENDING_TTL_MS,
      } satisfies PendingLoginReturn));
    } catch {
      // Storage non disponibile: dopo il login l'utente resta sulla shell.
    }
    return undefined;
  }

  /**
   * Chiamato a shell caricata. True = navigazione verso la satellite avviata: il
   * chiamante non prosegue verso la dashboard. Il ritorno vale una volta sola.
   */
  async resumePending(): Promise<boolean> {
    let raw: string | null;
    try {
      raw = this.browser.read();
      if (raw) this.browser.clear();
    } catch {
      return false;
    }
    const target = raw ? this.pendingTarget(raw) : null;
    if (!target || !this.catalogOrigins().has(target.origin) || !(await this.ensureAuthCookie())) return false;
    this.browser.replace(target.href);
    return true;
  }

  /** Logout: un ritorno in attesa non deve seguire il login di un'altra persona. */
  clear(): void {
    try {
      this.browser.clear();
    } catch {
      // Storage non disponibile: non c'è niente da togliere.
    }
  }

  private pendingTarget(raw: string): URL | null {
    try {
      const value = JSON.parse(raw) as Partial<PendingLoginReturn> | null;
      if (value?.version !== 1 || typeof value.url !== 'string' || typeof value.expiresAt !== 'number') return null;
      if (value.expiresAt <= Date.now() || value.expiresAt > Date.now() + PENDING_TTL_MS) return null;
      const current = this.browser.href();
      const target = resolveSuiteReturnUrl(value.url, current);
      return target && target.origin !== new URL(current).origin ? target : null;
    } catch {
      return null;
    }
  }

  /**
   * Origin delle app che la shell ha appena caricato per l'utente (URL già rimappati da
   * `mapDevURL`): il perimetro dei cookie comprende anche host che non sono app.
   */
  private catalogOrigins(): Set<string> {
    const origins = new Set<string>();
    const companies: unknown = this.user.userCompaniesList;
    for (const company of Array.isArray(companies) ? companies : []) {
      for (const app of Array.isArray(company?.apps) ? company.apps : []) {
        try {
          origins.add(new URL(app?.url).origin);
        } catch {
          // URL relativo (la shell stessa) o assente: non è un'altra app.
        }
      }
    }
    return origins;
  }

  /**
   * La satellite riconosce la sessione solo dal cookie `authToken`: senza, tornerebbe
   * qui. Si scrive il token dell'account attivo, come `MsalCookieSyncService`; MSAL lo
   * dà dalla cache se non è in scadenza, quindi la satellite non lo riceve agli ultimi
   * secondi. La satellite legge il primo `authToken` del cookie: deve essere questo.
   */
  private async ensureAuthCookie(): Promise<boolean> {
    const account = this.msal.instance.getActiveAccount();
    if (!account || !this.cookieWriter) return false;
    try {
      const result = await this.msal.instance.acquireTokenSilent({ scopes: msalScopes(this.config), account });
      if (!result?.accessToken) return false;
      this.cookieWriter.syncTokenToCookie(result.accessToken, account.username);
      return getAuthTokenFromCookie(this.config.cryptingKey) === result.accessToken;
    } catch {
      return false;
    }
  }

  /** Rotta della shell nell'URL, anche con hash routing (`/#/login` in locale). */
  private isLoginPage(target: URL): boolean {
    const path = target.hash.startsWith('#/') ? target.hash.slice(1).split('?')[0] : target.pathname;
    const loginRoute = this.config.loginRoute;
    return path === loginRoute || path.startsWith(`${loginRoute}/`);
  }
}
